import http from '@/utils/http';
import auditHttp from '@/utils/auditHttp';
import { IAM_ENDPOINTS } from '@/config/api';

/**
 * IAM (Identity and Access Management) API
 * 身份认证和访问管理相关接口
 */
export const iamApi = {
  /**
   * 用户登录
   * @param {object} credentials - 登录凭据 { username, password }
   * @returns {Promise}
   */
  login(credentials) {
    return http.post(IAM_ENDPOINTS.LOGIN, credentials);
  },

  /**
   * 用户注册
   * @param {object} userData - 用户注册信息
   * @returns {Promise}
   */
  register(userData) {
    return http.post(IAM_ENDPOINTS.REGISTER, userData);
  },

  /**
   * 刷新令牌
   * @param {string} refreshToken - 刷新令牌
   * @returns {Promise}
   */
  refreshToken(refreshToken) {
    return http.post(IAM_ENDPOINTS.REFRESH_TOKEN, { refreshToken });
  },

  /**
   * 用户登出
   * @returns {Promise}
   */
  logout() {
    return http.post(IAM_ENDPOINTS.LOGOUT);
  },

  /**
   * 获取当前用户信息
   * @returns {Promise}
   */
  getCurrentUser() {
    return http.get(IAM_ENDPOINTS.USER_PROFILE);
  },

  /**
   * 更新用户信息
   * @param {object} userData - 用户信息
   * @returns {Promise}
   */
  updateUserProfile(userData) {
    return http.put(IAM_ENDPOINTS.USER_PROFILE, userData);
  },

  /**
   * 修改密码
   * @param {object} passwordData - 密码信息 { oldPassword, newPassword }
   * @returns {Promise}
   */
  changePassword(passwordData) {
    return http.put(IAM_ENDPOINTS.CHANGE_PASSWORD, passwordData);
  },

  /**
   * 验证邮箱
   * @param {string} email - 邮箱地址
   * @param {string} code - 验证码
   * @returns {Promise}
   */
  verifyEmail(email, code) {
    return http.post(IAM_ENDPOINTS.VERIFY_EMAIL, { email, code });
  },

  /**
   * 发送验证码
   * @param {string} email - 邮箱地址
   * @param {string} type - 验证码类型 (register, reset-password, etc.)
   * @returns {Promise}
   */
  sendVerificationCode(email, type = 'register') {
    return http.post(IAM_ENDPOINTS.SEND_CODE, { email, type });
  },

  /**
   * 发送账号创建场景的邮箱验证码
   * @param {object} payload - 验证码请求 { target, channel }
   * @param {string} payload.target - 接收验证码的邮箱（Admin Email）
   * @param {string} payload.channel - 发送渠道，如 'EMAIL'
   * @returns {Promise}
   */
  sendAccountCreationCode(payload) {
    return http.post(IAM_ENDPOINTS.ACCOUNT_CREATION_CODE, payload);
  },

  /**
   * 重置密码
   * @param {object} resetData - 重置密码数据 { email, code, newPassword }
   * @returns {Promise}
   */
  resetPassword(resetData) {
    return http.post(IAM_ENDPOINTS.RESET_PASSWORD, resetData);
  },

  /**
   * 获取用户角色和权限
   * @returns {Promise}
   */
  getUserPermissions() {
    return http.get(IAM_ENDPOINTS.USER_PERMISSIONS);
  },

  /**
   * 检查令牌有效性
   * @param {string} token - 访问令牌
   * @returns {Promise}
   */
  validateToken(token) {
    return http.post(IAM_ENDPOINTS.VALIDATE_TOKEN, { token });
  },

  /**
   * 获取应用列表
   * @param {object} params - 查询参数
   * @returns {Promise}
   */
  getApps(params) {
    return http.get(IAM_ENDPOINTS.APPS, { params });
  },

  /**
   * Provision 应用
   * @param {string} accountId - 账户ID
   * @param {string} applicationCode - 应用编码
   * @returns {Promise}
   */
  provisionApp(accountId, applicationCode) {
    return http.post(IAM_ENDPOINTS.ACTIVATE_APP.replace('{id}', accountId).replace('{applicationCode}', applicationCode));
  },

  /**
   * Suspend 应用
   * @param {string} accountId - 账户ID
   * @param {string} applicationCode - 应用编码
   * @returns {Promise}
   */
  suspendApp(accountId, applicationCode) {
    return http.put(IAM_ENDPOINTS.SUSPEND_APP.replace('{id}', accountId).replace('{applicationCode}', applicationCode));
  },

  /**
   * Resume 应用
   * @param {string} accountId - 账户ID
   * @param {string} applicationCode - 应用编码
   * @returns {Promise}
   */
  resumeApp(accountId, applicationCode) {
    return http.put(IAM_ENDPOINTS.RESUME_APP.replace('{id}', accountId).replace('{applicationCode}', applicationCode));
  },

  /**
   * 获取应用开通面板数据
   * @param {string} accountId - 账户ID
   * @returns {Promise}
   */
  getApplicationPanel(accountId) {
    return http.get(IAM_ENDPOINTS.APPLICATION_PANEL.replace('{id}', accountId));
  },

  /**
   * Deprovision 应用
   * @param {string} accountId - 账户ID
   * @param {string} applicationCode - 应用编码
   * @returns {Promise}
   */
  deprovisionApp(accountId, applicationCode) {
    return http.put(IAM_ENDPOINTS.DEPROVISION_APP.replace('{id}', accountId).replace('{applicationCode}', applicationCode));
  },

  /**
   * 获取订阅信息（含审计席位）
   * @param {string} accountId - 账户ID
   * @returns {Promise}
   */
  getSubscription(accountId) {
    return auditHttp.get(IAM_ENDPOINTS.SUBSCRIPTION.replace('{id}', accountId));
  },

  /**
   * 重命名审计席位
   * @param {string} accountId - 账户ID
   * @param {string} seatId - 席位ID
   * @param {string} name - 新名称
   * @returns {Promise}
   */
  renameSeat(accountId, seatId, name) {
    return auditHttp.put(IAM_ENDPOINTS.RENAME_SEAT.replace('{id}', accountId).replace('{seatId}', seatId), { newName: name });
  },

  /**
   * 分配审计员到席位
   * @param {string} accountId - 账户ID
   * @param {string} seatId - 席位ID
   * @param {string} identityId - 被分配用户的身份ID
   * @param {string|null} [numeral=null] - 审计员编号（已注册 audit 用户传入其 auditUserNumeral，新注册传 null）
   * @param {boolean} [autoRegister=true] - 是否自动将 IAM 用户注册为 audit 用户；false 时 auditProfile 传 null，true 时携带完整 auditProfile
   * @returns {Promise}
   */
  assignAuditor(accountId, seatId, identityId, numeral = null, autoRegister = true) {
    const body = { identityId }
    if (autoRegister) {
      body.auditProfile = {
        numeral,
        companyOrDepartment: null,
        qualificationStatus: null,
        auditorRequirements: null,
        evidenceText: null,
        evidenceAttachmentIds: [],
        certificationNumber: null,
        leadAuditor: true,
        coAuditor: true,
        internalExpiryDate: null,
        externalExpiryDate: null,
        auditTypes: [],
        internalOrExternal: null
      }
    } else {
      body.auditProfile = null
    }
    return auditHttp.post(IAM_ENDPOINTS.ASSIGN_SEAT.replace('{id}', accountId).replace('{seatId}', seatId), body)
  },

  /**
   * 解除席位分配
   * @param {string} accountId - 账户ID
   * @param {string} seatId - 席位ID
   * @returns {Promise}
   */
  unassignAuditor(accountId, seatId) {
    return auditHttp.put(IAM_ENDPOINTS.RELEASE_SEAT.replace('{id}', accountId).replace('{seatId}', seatId));
  },

  /**
   * 获取账户下的身份（用户）列表
   * 用于审计席位分配时的用户下拉框
   * @param {string} accountId - 账户ID
   * @returns {Promise<Array>} 身份列表
   */
  getIdentities(accountId) {
    return auditHttp.get(IAM_ENDPOINTS.ACCOUNT_IDENTITIES.replace('{id}', accountId));
  }
};

export default iamApi;